import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Lock } from "lucide-react";

/**
 * CartDrawer
 * Desktop: docked panel in the lower-right corner (fixed, slides up).
 * Mobile: full-width bottom sheet.
 * Always mounted so the docked "Cart · N" launcher stays visible.
 */
export default function CartDrawer() {
  const { cart, open, setOpen, updateQty, removeItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const itemCount = cart?.count || 0;
  const subtotal = Number(cart?.subtotal || 0);

  // Auto-close when navigating to checkout or when the user opens dashboard/admin routes
  useEffect(() => {
    if (location.pathname.startsWith("/checkout") || location.pathname.startsWith("/order-success")) {
      setOpen(false);
    }
  }, [location.pathname, setOpen]);

  // Hide on admin routes entirely
  if (location.pathname.startsWith("/admin") || location.pathname === "/onboarding") return null;

  const goCheckout = () => {
    if (!user) { navigate("/login"); setOpen(false); return; }
    if (!user.onboarding_complete) { navigate("/onboarding"); setOpen(false); return; }
    if (itemCount === 0) return;
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {/* Docked launcher — always visible, lower-right on desktop, above bottom bar on mobile */}
      <button
        type="button"
        data-testid="cart-launcher"
        onClick={() => setOpen(!open)}
        className={`fixed z-40 bottom-5 right-5 h-12 pl-3 pr-4 rounded-full bg-slate-900 hover:bg-blue-600 text-white shadow-lg transition-colors flex items-center gap-2 ${open ? "opacity-0 pointer-events-none" : ""}`}
      >
        <div className="relative">
          <ShoppingBag className="w-5 h-5" strokeWidth={1.75}/>
          {itemCount > 0 && (
            <span data-testid="cart-launcher-badge" className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">
              {itemCount}
            </span>
          )}
        </div>
        <span className="text-sm font-medium">Cart{itemCount > 0 ? ` · $${subtotal.toFixed(2)}` : ""}</span>
      </button>

      {/* Backdrop on mobile only */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/30 md:bg-transparent md:pointer-events-none"
        />
      )}

      {/* Drawer panel */}
      <div
        data-testid="cart-drawer"
        className={`fixed z-50 bg-white border border-slate-200 shadow-2xl transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-[110%]"
        }
        bottom-0 left-0 right-0 rounded-t-2xl max-h-[85vh]
        md:bottom-5 md:right-5 md:left-auto md:top-auto md:w-[420px] md:rounded-md md:max-h-[80vh]`}
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
            <div className="font-display text-lg font-semibold">Your cart</div>
            <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">{itemCount} {itemCount === 1 ? "item" : "items"}</span>
          </div>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-2 rounded hover:bg-slate-100">
            <X className="w-4 h-4" strokeWidth={1.75}/>
          </button>
        </div>

        <div className="overflow-y-auto max-h-[52vh] px-4 py-3 space-y-3">
          {cart?.items?.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              Your cart is empty.
              <div className="mt-3">
                <Link to="/catalog" onClick={() => setOpen(false)} className="text-blue-700 text-sm underline">Browse catalog →</Link>
              </div>
            </div>
          ) : (
            cart?.items?.map((it) => (
              <div key={it.line_id} data-testid={`cart-line-${it.line_id}`} className="flex gap-3 border-b border-slate-100 pb-3 last:border-b-0">
                <div className="w-16 h-16 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex-shrink-0">
                  {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover"/> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-sm font-semibold text-slate-900 truncate">{it.name}</div>
                      {it.variant_label && (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{it.variant_label}</div>
                      )}
                    </div>
                    <button data-testid={`cart-remove-${it.line_id}`} onClick={() => removeItem(it.line_id)} className="p-1 rounded hover:bg-red-50 text-red-500" aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75}/>
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center border border-slate-200 rounded-md">
                      <button data-testid={`cart-decrement-${it.line_id}`} onClick={() => updateQty(it.line_id, Math.max(0, it.quantity - 1))} className="p-1.5 hover:bg-slate-50 disabled:opacity-40" aria-label="Decrease">
                        <Minus className="w-3 h-3" strokeWidth={1.75}/>
                      </button>
                      <div className="px-3 text-sm font-mono w-8 text-center">{it.quantity}</div>
                      <button data-testid={`cart-increment-${it.line_id}`} onClick={() => updateQty(it.line_id, it.quantity + 1)} className="p-1.5 hover:bg-slate-50" aria-label="Increase">
                        <Plus className="w-3 h-3" strokeWidth={1.75}/>
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-slate-500">${Number(it.unit_price).toFixed(2)} × {it.quantity}</div>
                      <div className="font-mono text-sm font-semibold text-slate-900">${Number(it.line_total).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-slate-200 p-4 bg-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Subtotal</div>
            <div data-testid="cart-subtotal" className="font-mono text-lg font-semibold text-slate-900">${subtotal.toFixed(2)}</div>
          </div>
          <Button
            data-testid="cart-checkout-btn"
            disabled={itemCount === 0}
            onClick={goCheckout}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            {!user ? (
              <><Lock className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Sign in to checkout</>
            ) : !user.onboarding_complete ? (
              <><Lock className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Complete profile</>
            ) : (
              <>Checkout <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75}/></>
            )}
          </Button>
          <div className="mt-2 text-[11px] text-slate-500 text-center">Submit an order request — no payment now. We'll email your invoice.</div>
        </div>
      </div>
    </>
  );
}
